import React, { useState, useEffect } from "react";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";
import { useNavigate } from "react-router-dom";
import CustomButton from "../pages/CustomButton";

const ViewDistributor = () => {
  const navigate = useNavigate();

  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [distributors, setDistributors] = useState([]);
  const [allDistributors, setAllDistributors] = useState([]);
  const [selectedDistributors, setSelectedDistributors] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [assignModal, setAssignModal] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [modalSelectedDivision, setModalSelectedDivision] = useState("");
  const [modalSelectedState, setModalSelectedState] = useState("");
  const [recentlyEdited, setRecentlyEdited] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetch(`${BASE_URL}/api/states`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => setStates(data || []))
      .catch((err) => console.error("Error fetching states:", err));

    fetch(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => {
        setDivisions(data || []);
      })
      .catch((err) => console.error("Error fetching divisions:", err));



    fetchAllDistributors();
  }, []);

  const fetchAllDistributors = () => {
    setIsLoading(true);
    fetch(`${BASE_URL}/api/distributors`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => {
        setAllDistributors(Array.isArray(data) ? data : []);
        setDistributors(Array.isArray(data) ? data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching distributors:", err);
        setAllDistributors([]);
        setDistributors([]);
        setIsLoading(false);
      });
  };

  const fetchFilteredDistributors = () => {
    setIsLoading(true);
    const params = new URLSearchParams();
    if (selectedState) params.append("state_id", selectedState);
    if (selectedDivision) params.append("division_id", selectedDivision);

    const url = `${BASE_URL}/api/distributors${params.toString() ? "?" + params.toString() : ""
      }`;

    fetch(url, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => {
        setDistributors(Array.isArray(data) ? data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching filtered distributors:", err);
        setDistributors([]);
        setIsLoading(false);
      });
  };

  const handleFilterChange = () => {
    if (!selectedState && !selectedDivision) {
      setDistributors(allDistributors);
      return;
    }
    fetchFilteredDistributors();
  };

  const handleActionClick = (distributor) => {
    if (!distributor || !distributor.id) {
      alert("Distributor id missing");
      return;
    }
    navigate(`/admin/add-distributors?id=${distributor.id}`);
  };

  const handleAssignCheckbox = (distributorId) => {
    setSelectedDistributors((prev) => {
      if (prev.includes(distributorId)) {
        return prev.filter((id) => id !== distributorId);
      } else {
        return [...prev, distributorId];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDistributors(distributors.map((d) => d.id));
    } else {
      setSelectedDistributors([]);
    }
  };

  const handleDelete = () => {
    if (selectedDistributors.length === 0) {
      alert("Please select at least one distributor to delete");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedDistributors.length} distributor(s)?`
      )
    ) {
      return;
    }

    setIsLoading(true);

    const deletePromises = selectedDistributors.map((id) =>
      fetch(`${BASE_URL}/api/distributors/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      }).then((res) => {
        if (!res.ok) {
          return res.text().then((t) => {
            throw new Error(t || `Failed to delete ${id}`);
          });
        }
        return id;
      })
    );

    Promise.allSettled(deletePromises)
      .then((results) => {
        const succeeded = results
          .filter((r) => r.status === "fulfilled")
          .map((r) => r.value);
        const failed = results.filter((r) => r.status === "rejected");

        if (succeeded.length > 0) {
          setDistributors((prev) =>
            prev.filter((d) => !succeeded.includes(d.id))
          );
          setAllDistributors((prev) =>
            prev.filter((d) => !succeeded.includes(d.id))
          );
        }

        const failedIds = failed
          .map((f) => {
            const msg = f.reason?.message || "";
            const m = msg.match(/(\d+)/);
            return m ? Number(m[1]) : null;
          })
          .filter(Boolean);

        setSelectedDistributors(failedIds);

        if (failed.length === 0) {
          alert("Selected distributors deleted successfully");
        } else {
          alert(`${succeeded.length} deleted, ${failed.length} failed`);
        }
      })
      .catch((err) => {
        console.error("Error deleting distributors:", err);
        alert("Error deleting distributors");
      })
      .finally(() => setIsLoading(false));
  };

  const handleEmployeeSelect = (e) => {
    setSelectedEmployee(e.target.value);
  };

  const openAssignModal = () => {
    if (selectedDistributors.length === 0) {
      alert("Please select at least one distributor");
      return;
    }
    setAssignModal(true);
  };

  const closeModal = () => {
    setAssignModal(false);
    setSelectedDistributors([]);
    setSelectedEmployee("");
    setModalSelectedDivision("");
    setModalSelectedState("");
    setFilteredEmployees([]);
  };

  const handleAssign = async () => {
    if (!selectedEmployee || selectedDistributors.length === 0) {
      alert("Please select both employee and at least one distributor");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/distributors/assign`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          employee_id: parseInt(selectedEmployee, 10),
          distributor_ids: selectedDistributors.map((id) => parseInt(id, 10)),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Assignment failed");
      }

      const result = await response.json();
      alert(result.message || "Assigned successfully");

      setRecentlyEdited(selectedDistributors);
      setTimeout(() => setRecentlyEdited([]), 3000);
      setSelectedDistributors([]);
      setSelectedEmployee("");
      setAssignModal(false);

      fetchAllDistributors();
    } catch (error) {
      console.error("Assignment failed:", error);
      alert(`Assignment failed: ${error.message}`);
    }
  };

  return (
    <div className="w-full">
      {isLoading && <Loader />}

      <div
        className={`main-container ${isLoading ? "blur-sm pointer-events-none" : ""
          }`}
      >
        {/* full width wrapper with border like screenshot */}
        <div className="w-full mx-auto min-h-[67vh] p-4 font-sans border-2 border-gray-300 rounded-lg bg-white">
          {/* sticky header (full width) */}
          <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200">
            <div className="py-4">
              <h1 className="text-2xl font-bold text-center text-blue-700">
                Distributor List
              </h1>
            </div>
          </div>

          {/* filters panel: two dropdowns in first row, second row for GO + action buttons */}
          <div className="mt-6">
            <div className="w-full bg-gray-50 rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                {/* State */}
                <div className="flex items-center gap-3">
                  <label className="font-semibold whitespace-nowrap">State</label>
                  <select
                    onChange={(e) => setSelectedState(e.target.value)}
                    value={selectedState}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">All</option>
                    {states.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Division */}
                <div className="flex items-center gap-3">
                  <label className="font-semibold whitespace-nowrap">Division</label>
                  <select
                    onChange={(e) => setSelectedDivision(e.target.value)}
                    value={selectedDivision}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">All</option>
                    {divisions.map((div) => (
                      <option key={div.id} value={div.id}>
                        {div.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>


              {/* second row: centered GO button and conditional action buttons */}
              <div className="mt-6 flex flex-col items-center">
                <div className="w-full sm:w-auto">
                  <CustomButton
                    className="px-6 py-2 bg-green-500 text-white rounded-md font-medium shadow"
                    onClick={handleFilterChange}
                    disabled={isLoading}
                  >
                    {isLoading ? "Loading..." : "GO"}
                  </CustomButton>
                </div>

                {/* only show these when selection exists, placed below GO as in screenshot */}
                {selectedDistributors.length > 0 && (
                  <div className="mt-3 flex items-center justify-center gap-3">
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded font-semibold whitespace-nowrap text-center"
                      onClick={openAssignModal}
                      disabled={isLoading}
                    >
                      Assign ({selectedDistributors.length})
                    </button>

                    <button
                      className="px-4 py-2 bg-red-600 text-white rounded font-semibold whitespace-nowrap text-center"
                      onClick={handleDelete}
                      disabled={isLoading}
                    >
                      Delete ({selectedDistributors.length})
                    </button>
                  </div>

                )}
              </div>
            </div>
          </div>

          {/* assign modal (unchanged logic) */}
          {assignModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg w-full max-w-3xl shadow-lg">
                <h3 className="text-lg font-semibold mb-4">
                  Assign Employee to {selectedDistributors.length}{" "}
                  Distributor(s)
                </h3>

                <div className="w-full mb-4">
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block font-semibold mb-2">
                        Select State
                      </label>
                      <select
                        value={modalSelectedState}
                        onChange={(e) => {
                          const stateId = e.target.value;
                          setModalSelectedState(stateId);
                          setModalSelectedDivision("");
                          setSelectedEmployee("");
                          setFilteredEmployees([]);

                          if (stateId) {
                            fetch(
                              `${BASE_URL}/api/divisions?state_id=${stateId}`,
                              { headers: getAuthHeaders() }
                            )
                              .then((res) => res.json())
                              .then((data) => setDivisions(data || []))
                              .catch((err) =>
                                console.error("Error fetching divisions:", err)
                              );
                          }
                        }}
                        className="w-full px-3 py-2 border rounded"
                      >
                        <option value="">Select State</option>
                        {states.map((state) => (
                          <option key={state.id} value={state.id}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex-1 min-w-[200px]">
                      <label className="block font-semibold mb-2">
                        Select Division
                      </label>
                      <select
                        value={modalSelectedDivision}
                        onChange={(e) => {
                          const divisionId = e.target.value;
                          setModalSelectedDivision(divisionId);
                          setSelectedEmployee("");

                          if (divisionId && modalSelectedState) {
                            const selectedDivision = divisions.find(
                              (d) => d.id === parseInt(divisionId, 10)
                            );
                            const divisionName = selectedDivision?.name;

                            if (divisionName) {
                              fetch(
                                `${BASE_URL}/api/employees?state_id=${modalSelectedState}&division=${encodeURIComponent(
                                  divisionName
                                )}`,
                                { headers: getAuthHeaders() }
                              )
                                .then((res) => res.json())
                                .then((data) =>
                                  setFilteredEmployees(data || [])
                                )
                                .catch((err) =>
                                  console.error(
                                    "Error fetching employees:",
                                    err
                                  )
                                );
                            }
                          } else {
                            setFilteredEmployees([]);
                          }
                        }}
                        disabled={!modalSelectedState}
                        className="w-full px-3 py-2 border rounded"
                      >
                        <option value="">Select Division</option>
                        {divisions.map((div) => (
                          <option key={div.id} value={div.id}>
                            {div.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex-1 min-w-[200px]">
                      <label className="block font-semibold mb-2">
                        Select Employee:
                      </label>
                      <select
                        value={selectedEmployee}
                        onChange={handleEmployeeSelect}
                        disabled={
                          !modalSelectedDivision ||
                          filteredEmployees.length === 0
                        }
                        className="w-full px-3 py-2 border rounded"
                      >
                        <option value="">
                          {filteredEmployees.length === 0
                            ? modalSelectedDivision
                              ? "No employees in this division"
                              : "Select a division first"
                            : "Select Employee"}
                        </option>
                        {filteredEmployees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.first_name} {emp.last_name} | {emp.employee}  ({emp.designation}{" "}
                            | {emp.division} | {emp.headquarter_name} |{" "}
                            {emp.state_name})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded"
                    onClick={handleAssign}
                  >
                    Assign
                  </button>
                  <button
                    className="px-4 py-2 bg-red-500 text-white rounded"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* table container: full width inside wrapper */}
          <div className="mt-8 w-full overflow-x-auto p-4 bg-white border border-gray-300 rounded shadow-sm">
            <table className="min-w-full border-collapse">
              <thead className="bg-[#2b79e6] text-white">
                <tr>
                  <th className="p-2 text-center">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={
                        selectedDistributors.length === distributors.length &&
                        distributors.length > 0
                      }
                      className="transform scale-125"
                    />
                  </th>
                  <th className="p-2 text-center">S.No</th>
                  <th className="p-2 text-center">ID</th>
                  <th className="p-2 text-center">Distributor Name</th>
                  <th className="p-2 text-center">Contact Person</th>
                  <th className="p-2 text-center">Division</th>
                  <th className="p-2 text-center">State</th>
                  <th className="p-2 text-center">City</th>
                  <th className="p-2 text-center">Phone</th>
                  <th className="p-2 text-center">GSTIN</th>
                  <th className="p-2 text-center">Address</th>
                  <th className="p-2 text-center">Active</th>
                  <th className="p-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(distributors) && distributors.length > 0 ? (
                  distributors.map((d, idx) => (
                    <tr
                      key={d.id || idx}
                      className={`${recentlyEdited.includes(d.id) ? "bg-yellow-200" : ""
                        }`}
                    >
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedDistributors.includes(d.id)}
                          onChange={() => handleAssignCheckbox(d.id)}
                          className="transform scale-125"
                        />
                      </td>
                      <td className="p-2 text-center">{idx + 1}</td>
                      <td className="p-2 text-center">{d.id}</td>
                      <td className="p-2">
                        {d.distributor_name || d.distributorName || "N/A"}
                      </td>
                      <td className="p-2">
                        {d.contact_person || d.contactPerson || "N/A"}
                      </td>
                      <td className="p-2 text-center">
                        {d.division_name || d.division || "N/A"}
                      </td>
                      <td className="p-2 text-center">
                        {d.state_name || d.state || "N/A"}
                      </td>
                      <td className="p-2 text-center">
                        {d.city_name || d.city || "N/A"}
                      </td>
                      <td className="p-2 text-center">
                        {d.mobile || d.phone || "N/A"}
                      </td>
                      <td className="p-2 text-center">{d.gstin || "N/A"}</td>
                      <td className="p-2">{d.address || "N/A"}</td>
                      <td className="p-2 text-center">
                        {typeof d.is_active !== "undefined"
                          ? d.is_active
                            ? "Yes"
                            : "No"
                          : "N/A"}
                      </td>
                      <td className="p-2 text-center">
                        <button
                          className="w-full px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                          onClick={() => handleActionClick(d)}
                        >
                          View/Edit
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="13" className="p-4 text-center">
                      No distributors found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewDistributor;
